/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, FrameInset, Theme } from '@nexigendigital/nxui'
import { useViewCartQuery } from 'api/shop'
import { setCheckoutStep } from 'store/slices/shopSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'

import Nav from 'components/nav/nav'
import PaymentMethods from 'containers/shop/checkout/paymentMethods'
import CheckoutConfirmation from 'containers/shop/checkout/checkoutConfirmation'
import CartSummary from 'containers/shop/cart/cartSummary'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Checkout() {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { cart, paymentMethod, checkoutStep } = useAppSelector((state) => state.shop)
    const hasPaymentMethod = paymentMethod !== null
    useViewCartQuery(cart?.uuid, { skip: !cart?.uuid })

    useEffect(() => {
        if (!cart?.uuid) {
            navigate('/shop')
        }
    }, [cart, dispatch, navigate])

    function checkout() {
        return (
            <CheckoutWrapper>
                <Nav>
                    <Anchor
                        className={`${checkoutStep === 'payment' ? 'selected' : ''}`}
                        onClick={() => dispatch(setCheckoutStep('payment'))}
                    >
                        Payment Method
                    </Anchor>
                    <Anchor
                        disabled={!hasPaymentMethod}
                        className={`${checkoutStep === 'checkout' ? 'selected' : ''}`}
                        onClick={() => dispatch(setCheckoutStep('checkout'))}
                    >
                        Checkout
                    </Anchor>
                </Nav>
            </CheckoutWrapper>
        )
    }

    const renderStep = () => {
        switch (checkoutStep) {
            case 'checkout':
                return <CheckoutConfirmation/>
            case 'payment':
            default:
                return (
                    <>
                        <PaymentMethods/>
                        <CartSummary/>
                    </>
                )
        }
    }

    return (
        <FrameInset
            inset={'split'}
            location={{ pathname: '/Checkout' }}
            left={{
                render: checkout()
            }}
            right={{
                render: <StyledCheckout>{renderStep()}</StyledCheckout>
            }}
        />
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const CheckoutWrapper = styled.div`
    width: 100%;
`

const StyledCheckout = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 20px;

    > div {
        width: 100%;

        &:nth-child(2) {
            width: 398px;

            ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
                width: 100%;
            }
        }
    }
`
